import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { supabase } from "../lib/supabase"
import { DateFilter } from "../types/filters"

export interface AssessmentFalloffDataPoint {
  question: number
  completed: number
  dropped: number
}

/**
 * Hook to fetch assessment falloff data from the metrics-assessment-falloff endpoint
 * @param assessmentType The type of assessment to fetch data for ('sjt' or 'driver-onroad-test')
 * @param dateFilter Optional date range filter
 * @returns Query result with assessment falloff data for stacked bar chart
 */
export const useAssessmentFalloffData = (
  assessmentType: "sjt" | "driver-onroad-test",
  dateFilter?: DateFilter,
  jobId?: string,
) => {
  const query = useQuery<AssessmentFalloffDataPoint[]>({
    queryKey: ["assessmentFalloff", assessmentType, dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-assessment-falloff", {
        body: {
          assessmentType,
          startDate: dateFilter?.startDate || null,
          endDate: dateFilter?.endDate || null,
          jobId: jobId ?? null,
        },
      })

      if (error) {
        throw new Error(`Error fetching assessment falloff data: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
